import React, { useContext, useState, useEffect, useRef } from "react";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    IconButton,
    Paper,
    Typography,
    TableContainer,
    Tooltip,
    Switch,
    Box,
    Alert,
    TablePagination,
    InputAdornment,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { ItemsContext } from "../App";
import { getApiToken } from "../authUtils";
import { useMsal } from "@azure/msal-react";
import DownloadBillpdf from "../components/DownloadBillpdf";
import { gstRate, withGst } from "../shared/gst";

const urlconfig = require('../config/urlconfig');


const emptyMarketingForm = () => ({
    actualMarketingCost: "",
    marketingTransactionDate: "",
    marketingPaid: false,
    fileUrlMarketing: null,
    file: null,
});

const marketingTotal = (details) =>
    (details || []).reduce((sum, d) => sum + (Number(d.actualMarketingCost) || 0), 0);

const MarketingDashboard = () => {
    const { items, refreshItems } = useContext(ItemsContext);

    const { instance, accounts } = useMsal();
    const account = accounts && accounts.length > 0 ? accounts[0] : null;

    const [openDialog, setOpenDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [fileUrls, setFileUrls] = useState({});
    const [marketingForms, setMarketingForms] = useState([emptyMarketingForm()]);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const pdfMarketingRef = useRef();
    const pdfMarketingDownloadRef = useRef();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500); // 500ms debounce

        return () => clearTimeout(handler);
    }, [searchQuery]);

    // Fetch items when debounced search / page / rowsPerPage changes
    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            try {
                if (!mounted) return;
                setLoading(true);
                await refreshItems(debouncedSearchQuery, page, rowsPerPage);
            } catch (err) {
                console.error("Error refreshing items:", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchData();
        return () => {
            mounted = false;
        };
    }, [debouncedSearchQuery, page, rowsPerPage, refreshItems]);

    const handleMarketingAdd = (item) => {
        setSelectedItem(item);
        setOpenDialog(true);

        if (item.marketingDetails && item.marketingDetails.length > 0) {
            setMarketingForms(
                item.marketingDetails.map(d => ({
                    ...emptyMarketingForm(),
                    actualMarketingCost: d.actualMarketingCost ?? "",
                    marketingTransactionDate: d.marketingTransactionDate ? d.marketingTransactionDate.split("T")[0] : "",
                    marketingPaid: !!d.marketingPaid,
                    fileUrlMarketing: d.fileUrlMarketing || null,
                }))
            );
        } else {
            setMarketingForms([emptyMarketingForm()]);
        }
    };

    const handleViewMarketing = (item) => {
        setSelectedItem(item);
        setFileUrls({}); // reset previously fetched urls
        setOpenViewDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setOpenViewDialog(false);
        setSelectedItem(null);
        setFileUrls({});
    };

    const handleAddAnotherMarketing = () => {
        setMarketingForms([...marketingForms, emptyMarketingForm()]);
    };

    const handleFormChange = (index, field, value) => {
        const updatedForms = [...marketingForms];
        if (field === "file") {
            updatedForms[index] = { ...updatedForms[index], file: value, fileUrlMarketing: null };
        } else {
            updatedForms[index] = { ...updatedForms[index], [field]: value };
        }
        setMarketingForms(updatedForms);
    };

    const handleRemoveForm = (index) => {
        if (marketingForms[index].marketingPaid === true) {
            alert("Cannot remove the form, as marketing is paid");
            return;
        }
        const updatedForms = marketingForms.filter((_, i) => i !== index);
        setMarketingForms(updatedForms.length > 0 ? updatedForms : [emptyMarketingForm()]);
    };

    const sendAuthenticatedRequest = async (url, method, body) => {
        const accessToken = await getApiToken(instance, account);
        const response = await fetch(url, {
            method,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({
                message: response.statusText,
            }));
            throw new Error(`API Error: ${response.status} - ${errorData.message || "Unknown error"}`);
        }

        const contentType = response.headers.get("content-type");
        return contentType?.includes("application/json") ? response.json() : response.text();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedItem) return;

        try {
            const accessToken = await getApiToken(instance, account);

            const updatedMarketingDetails = await Promise.all(
                marketingForms.map(async (formData) => {
                    let fileUrlMarketing = formData.fileUrlMarketing || null;
                    if (formData.file) {
                        const uploadData = new FormData();
                        uploadData.append("file", formData.file);
                        const res = await fetch(
                            `${urlconfig.backend_api_base_url}/api/v2/marketing/${selectedItem.id}/upload`,
                            {
                                method: "POST",
                                headers: { Authorization: `Bearer ${accessToken}` },
                                body: uploadData,
                            }
                        );
                        if (!res.ok) throw new Error("File upload failed");
                        fileUrlMarketing = await res.text();
                    }

                    return {
                        actualMarketingCost: Number(formData.actualMarketingCost) || 0,
                        marketingTransactionDate: formData.marketingTransactionDate || undefined,
                        marketingPaid: !!formData.marketingPaid,
                        fileUrlMarketing,
                    };
                })
            );

            await sendAuthenticatedRequest(
                `${urlconfig.backend_api_base_url}/api/v2/edit-costsheet/${selectedItem.id || selectedItem._id}`,
                "PUT",
                { marketingDetails: updatedMarketingDetails }
            );

            // refresh with current search/page/size
            await refreshItems(debouncedSearchQuery, page, rowsPerPage);

            setSelectedItem(null);
            setOpenDialog(false);
            setMarketingForms([emptyMarketingForm()]);
        } catch (err) {
            console.error("Update failed:", err);
            alert("Failed to save marketing details.");
        }
    };

    // Fetch bill URLs when opening view dialog
    useEffect(() => {
        if (!openViewDialog || !selectedItem?.marketingDetails) {
            setFileUrls({});
            return;
        }
        const fetchUrls = async () => {
            const newUrls = {};
            try {
                const accessToken = await getApiToken(instance, account);
                for (const d of selectedItem.marketingDetails) {
                    if (!d.fileUrlMarketing) continue;
                    try {
                        const res = await fetch(
                            `${urlconfig.backend_api_base_url}/api/v2/marketing/${selectedItem.id}/bill?key=${encodeURIComponent(d.fileUrlMarketing)}`,
                            {
                                method: "GET",
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${accessToken}`,
                                },
                            }
                        );
                        if (!res.ok) throw new Error("Failed to fetch file URL");
                        const data = await res.json();
                        newUrls[d.fileUrlMarketing] = data.url;
                    } catch (error) {
                        console.error("Error fetching file URL for", d.fileUrlMarketing, error);
                    }
                }
            } catch (err) {
                console.error("Error getting access token for file fetch", err);
            } finally {
                setFileUrls(newUrls);
            }
        };
        fetchUrls();
    }, [openViewDialog, selectedItem, instance, account]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(0);
    };

    return (
        <>
            <h1>Marketing Dashboard</h1>
            <p>Manage marketing details for sales orders.</p>

            <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Sales Order No, Client Name, SPOC, Details..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={() => {
                                    setSearchQuery('');
                                    setDebouncedSearchQuery('');
                                    setPage(0);
                                }}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Paper sx={{ width: '100%', mt: 2 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table stickyHeader aria-label="marketing table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Creation Date</TableCell>
                                    <TableCell>SO No.<br />Created By</TableCell>
                                    <TableCell>Client Name<br />Sales POC</TableCell>
                                    <TableCell>Budgeted Marketing Cost<br />incl. GST (₹)</TableCell>
                                    <TableCell>Actual Marketing Cost<br />incl. GST (₹)</TableCell>
                                    <TableCell>Variance (₹)</TableCell>
                                    <TableCell>Variance (%)</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.content && items.content.length > 0 ? (
                                    items.content.map((item, index) => {
                                        const gstPercent = gstRate(item, 'marketingGST');
                                        // Vendor bills already include GST, so the budget gets GST added and
                                        // the variance compares like with like
                                        const budgetedCost = withGst(item.marketingTotalPp, gstPercent);
                                        const actualCost = marketingTotal(item.marketingDetails);
                                        const variance = budgetedCost - actualCost;
                                        const variancePercent =
                                            budgetedCost === 0
                                                ? actualCost === 0
                                                    ? "0%"
                                                    : "-100%"
                                                : ((variance / budgetedCost) * 100).toFixed(2) + "%";

                                        return (
                                            <TableRow key={index}>
                                                <TableCell>{item.createdAT ? new Date(item.createdAT).toLocaleDateString() : "N/A"}</TableCell>
                                                <TableCell>
                                                    {item.salesOrderNo || "N/A"}
                                                    <div>
                                                        <Typography fontSize="small" fontWeight="bold">{item.createdBy || "N/A"}</Typography>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {item.clientName || "N/A"}
                                                    <div>
                                                        <Typography fontSize="small" fontWeight="bold">{item.salesPOC || "N/A"}</Typography>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    ₹{budgetedCost.toFixed(2)}
                                                    <Typography variant="caption" display="block" color="text.secondary">GST {gstPercent}%</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {item.marketingDetails?.length > 0 ? (
                                                        <Typography fontWeight="bold" color="success.main">
                                                            ₹{actualCost.toFixed(2)}
                                                        </Typography>
                                                    ) : (
                                                        "0.00"
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ color: variance > 0 ? 'green' : variance < 0 ? 'red' : 'inherit', fontWeight: variance !== 0 ? 'bold' : 'normal' }}>
                                                    ₹{variance.toFixed(2)}
                                                </TableCell>
                                                <TableCell>{variancePercent}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={1}>
                                                        <Button variant="contained" onClick={() => handleMarketingAdd(item)}>Edit</Button>
                                                        <Button variant="outlined" onClick={() => handleViewMarketing(item)}>View</Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Alert severity="info">
                                                {searchQuery
                                                    ? "No matching sales orders found for your search."
                                                    : "No sales orders available."}
                                            </Alert>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div"
                    count={items.totalElements || 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Marketing Dialog */}
            <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Add Marketing {selectedItem?.salesOrderNo && `for ${selectedItem.salesOrderNo}`}</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="Client Name"
                            fullWidth
                            margin="normal"
                            value={selectedItem?.clientName || ""}
                            InputProps={{ readOnly: true }}
                            helperText="From the cost sheet"
                        />
                        <TextField
                            label="Sales POC"
                            fullWidth
                            margin="normal"
                            value={selectedItem?.salesPOC || ""}
                            InputProps={{ readOnly: true }}
                            helperText="From the cost sheet"
                            sx={{ mb: 2 }}
                        />
                        {marketingForms.map((form, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, position: "relative", backgroundColor: '#f5f5f5' }} elevation={3}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveForm(index)}
                                    sx={{ position: "absolute", top: 5, right: 5, color: "red", fontSize: "16px" }}
                                    disabled={marketingForms.length === 1}
                                >
                                    <CloseIcon />
                                </IconButton>

                                <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <Grid item>Unpaid</Grid>
                                    <Grid item>
                                        <Tooltip
                                            title={form.marketingPaid ? "Once changed to paid, cannot be reverted" : ""}
                                            arrow
                                        >
                                            <span>
                                                <Switch
                                                    checked={form.marketingPaid}
                                                    onChange={() => {
                                                        if (!form.marketingPaid) {
                                                            handleFormChange(index, "marketingPaid", true);
                                                        }
                                                    }}
                                                    disabled={form.marketingPaid}
                                                />
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>Paid</Grid>
                                </Grid>

                                <TextField
                                    label="Actual Marketing Cost (₹)"
                                    type="number"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={form.actualMarketingCost}
                                    onChange={(e) => handleFormChange(index, "actualMarketingCost", e.target.value)}
                                />
                                <TextField
                                    label="Transaction Date"
                                    type="date"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={form.marketingTransactionDate}
                                    onChange={(e) => handleFormChange(index, "marketingTransactionDate", e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />

                                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: "bold", color: "blue" }}>
                                    Upload Marketing Bill:
                                </Typography>
                                <TextField
                                    type="file"
                                    inputProps={{ accept: "image/*" }}
                                    fullWidth
                                    margin="normal"
                                    onChange={(e) => handleFormChange(index, "file", e.target.files[0])}
                                />
                                {form.file && (
                                    <Box mt={1}>
                                        <Typography variant="body2">Selected File: {form.file.name}</Typography>
                                    </Box>
                                )}
                            </Paper>
                        ))}

                        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
                            <Grid item>
                                <Button variant="contained" onClick={handleAddAnotherMarketing}>
                                    Add Another Marketing
                                </Button>
                            </Grid>
                        </Grid>

                        <DialogActions>
                            <Button onClick={handleClose} color="secondary">Cancel</Button>
                            <Button type="submit" variant="contained" color="primary">Submit</Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>

            {/* View Marketing Dialog */}
            <Dialog open={openViewDialog} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    Marketing Details for {selectedItem?.salesOrderNo || "N/A"}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography><strong>Client Name:</strong> {selectedItem?.clientName || "N/A"}</Typography>
                    <Typography sx={{ mb: 2 }}><strong>Sales POC:</strong> {selectedItem?.salesPOC || "N/A"}</Typography>
                    {selectedItem?.marketingDetails && selectedItem.marketingDetails.length > 0 ? (
                        selectedItem.marketingDetails.map((d, index) => (
                            <Paper key={index} elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                                <Typography><strong>Actual Marketing Cost (₹):</strong> {d.actualMarketingCost ?? "N/A"}</Typography>
                                <Typography><strong>Transaction Date:</strong> {d.marketingTransactionDate?.split("T")[0] || "N/A"}</Typography>

                                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: "bold", color: "blue" }}>
                                    Marketing Bill:
                                </Typography>

                                {d.fileUrlMarketing ? (
                                    fileUrls[d.fileUrlMarketing] ? (
                                        <img
                                            src={fileUrls[d.fileUrlMarketing]}
                                            alt="Marketing Bill"
                                            crossOrigin="anonymous"
                                            style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                    ) : (
                                        <Typography variant="body2" sx={{ color: "orange" }}>
                                            Loading...
                                        </Typography>
                                    )
                                ) : (
                                    <Typography variant="body2" sx={{ color: "orange" }}>
                                        No bill uploaded
                                    </Typography>
                                )}

                                <Typography>
                                    <strong>Payment Status:</strong>{" "}
                                    <Typography component="span" sx={{ color: d.marketingPaid ? "green" : "red", fontWeight: "bold" }}>
                                        {d.marketingPaid ? "Paid" : "Unpaid"}
                                    </Typography>
                                </Typography>
                            </Paper>
                        ))
                    ) : (
                        <Typography>No marketing details available.</Typography>
                    )}
                </DialogContent>

                {selectedItem?.marketingDetails?.length > 0 && (
                    <Typography sx={{ p: 2, fontWeight: "bold", color: "green", textAlign: "right" }}>
                        Total Marketing Cost: ₹{marketingTotal(selectedItem.marketingDetails).toFixed(2)} (incl. GST)
                    </Typography>
                )}

                <DownloadBillpdf
                    ref={pdfMarketingDownloadRef}
                    pdfContentRef={pdfMarketingRef}
                    fileName={`Marketing_Details_${selectedItem?.salesOrderNo}.pdf`}
                />
                <DialogActions>
                    <Button variant="contained" color="success" onClick={() => pdfMarketingDownloadRef.current?.downloadPDF()}
                        disabled={!selectedItem?.marketingDetails?.length}>
                        Download Marketing Details
                    </Button>
                    <Button onClick={handleClose} color="primary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Download Marketing detail PDF UI */}
            <div style={{ position: "absolute", top: "-9999px", left: "-9999px", opacity: 0, pointerEvents: "none" }}>
                <div ref={pdfMarketingRef} style={{ width: "600px", padding: "20px" }}>
                    <h2>Marketing Details</h2>
                    <p><strong>Sales Order:</strong> {selectedItem?.salesOrderNo || "N/A"}</p>
                    <p><strong>Client Name:</strong> {selectedItem?.clientName || "N/A"}</p>
                    <p><strong>Sales POC:</strong> {selectedItem?.salesPOC || "N/A"}</p>
                    <hr />

                    {selectedItem?.marketingDetails?.length > 0 &&
                        selectedItem.marketingDetails.map((d, index) => (
                            <div key={index} style={{ marginBottom: "25px" }}>
                                <p><strong>Actual Marketing Cost:</strong> ₹{d.actualMarketingCost ?? "N/A"}</p>
                                <p><strong>Transaction Date:</strong> {d.marketingTransactionDate?.split("T")[0] || "N/A"}</p>

                                {d.fileUrlMarketing && fileUrls[d.fileUrlMarketing] && (
                                    <img
                                        src={fileUrls[d.fileUrlMarketing]}
                                        alt="Marketing Bill"
                                        style={{ width: "100%", marginTop: "10px", borderRadius: "6px", border: "1px solid #ccc" }}
                                    />
                                )}

                                <p>
                                    <strong>Payment Status:</strong>{" "}
                                    <span style={{ color: d.marketingPaid ? "green" : "red", fontWeight: "bold" }}>
                                        {d.marketingPaid ? "Paid" : "Unpaid"}
                                    </span>
                                </p>
                                <hr />
                            </div>
                        ))}

                    {selectedItem?.marketingDetails?.length > 0 && (
                        <h3 style={{ textAlign: "right", marginTop: "20px" }}>
                            Total Marketing Cost: ₹{marketingTotal(selectedItem.marketingDetails).toFixed(2)} (incl. GST)
                        </h3>
                    )}
                </div>
            </div>
        </>
    );
};

export default MarketingDashboard;
